import React, { useState, useEffect } from "react";
import { FaTrash, FaPlus } from "react-icons/fa";
import axios from "axios";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import { ToastContainer, toast } from "react-toastify";

const API_URL = "https://api.muktihospital.com";
const API_KEY = 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079';

// Configure axios with default headers
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "x-api-key": API_KEY,
    "Content-Type": "application/json",
  },
});

// Helper function to safely parse translations data
const parseTranslationsData = (translations) => {
  if (typeof translations === 'string') {
    try {
      const parsed = JSON.parse(translations);
      return parsed;
    } catch (error) {
      console.error('Error parsing translations JSON:', error);
      return {
        en: { 
          phone: "",
          menus: [
            {
              title: "",
              buttonTitle: "",
              link: "",
              openInNewTab: false,
              order: 1,
              parent: "",
              status: "active",
              isParent: true
            }
          ]
        },
        bn: {
          phone: "",
          menus: [
            {
              title: "",
              buttonTitle: "",
              link: "",
              openInNewTab: false,
              order: 1,
              parent: "",
              status: "active",
              isParent: true
            }
          ]
        }
      };
    }
  }
  return translations || {
    en: { 
      phone: "",
      menus: [
        {
          title: "",
          buttonTitle: "",
          link: "",
          openInNewTab: false,
          order: 1,
          parent: "",
          status: "active",
          isParent: true
        }
      ]
    },
    bn: {
      phone: "",
      menus: [
        {
          title: "",
          buttonTitle: "",
          link: "",
          openInNewTab: false,
          order: 1,
          parent: "",
          status: "active",
          isParent: true
        }
      ]
    }
  };
};

const ImageUpload = ({ label, file, setFile, imageUrl }) => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.size > MAX_FILE_SIZE) {
      toast.error(`File size too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }
    setFile(selectedFile);
  };

  const removeImage = () => {
    setFile(null);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      {file && (
        <div className="mt-4 relative">
          <img
            src={URL.createObjectURL(file)}
            alt="Uploaded"
            className="border rounded-md w-40 h-32 object-cover"
          />
          <button
            type="button"
            onClick={removeImage}
            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full"
          >
            <FaTrash />
          </button>
        </div>
      )}
      {!file && imageUrl && (
        <div className="mt-4 relative">
          <img
            src={`${API_URL}${imageUrl}`}
            alt="Current"
            className="border rounded-md w-40 h-32 object-cover"
          />
        </div>
      )}
       <div className="mt-4 w-full">
        <p className="text-[10px] text-gray-600 mt-1">
          Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB. Allowed types: JPEG, PNG, GIF, WEBP
        </p>
        <input
          type="file"
          accept="image/*"
          className="border p-2 rounded w-full"
          id={`upload-${label}`}
          onChange={handleFileUpload}
        />
      </div>
    </div>
  );
};

const Header = () => {
  const [language, setLanguage] = useState("en");
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  
  // State for the header with translations including menus
  const [headerData, setHeaderData] = useState({
    logo: null,
    contactIcon: null,
    translations: {
      en: { 
        phone: "",
        menus: [
          {
            title: "",
            buttonTitle: "",
            link: "",
            openInNewTab: false,
            order: 1,
            parent: "",
            status: "active",
            isParent: true
          }
        ]
      },
      bn: {
        phone: "",
        menus: [
          {
            title: "",
            buttonTitle: "",
            link: "",
            openInNewTab: false,
            order: 1,
            parent: "",
            status: "active",
            isParent: true
          }
        ]
      }
    }
  });

  // Store for existing header data from API
  const [existingHeaderData, setExistingHeaderData] = useState(null);

  // Fetch header data on component mount
  useEffect(() => {
    fetchHeaderData();
  }, []);

  // Fetch header data from API
  const fetchHeaderData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/header');
      console.log('Header data received:', response.data);
      
      setExistingHeaderData(response.data);
      
      // Initialize form with existing data
      if (response.data) {
        setIsEditing(true);
        
        // Parse translations from JSON string to object
        const parsedTranslations = parseTranslationsData(response.data.translations);
        console.log('Parsed translations:', parsedTranslations);
        
        // Add isParent flag to existing menu items
        let translationsWithParentFlag = { ...parsedTranslations };
        
        // Process each language
        Object.keys(translationsWithParentFlag).forEach(lang => {
          if (translationsWithParentFlag[lang] && translationsWithParentFlag[lang].menus) {
            translationsWithParentFlag[lang].menus = translationsWithParentFlag[lang].menus.map(menu => ({
              ...menu,
              isParent: !menu.parent || menu.parent === ""
            }));
          } else {
            // Initialize if menus don't exist
            translationsWithParentFlag[lang] = {
              phone: translationsWithParentFlag[lang]?.phone || "",
              menus: [
                {
                  title: "",
                  buttonTitle: "",
                  link: "",
                  openInNewTab: false,
                  order: 1,
                  parent: "",
                  status: "active",
                  isParent: true
                }
              ]
            };
          }
        });
        
        // Create a new headerData object with the processed data
        setHeaderData({
          logo: null, // We store the file object for uploads
          contactIcon: null, // We store the file object for uploads
          translations: translationsWithParentFlag
        });
        
        console.log('Updated headerData:', {
          logo: null,
          contactIcon: null,
          translations: translationsWithParentFlag
        });
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        // Header doesn't exist yet, leave form in "add" mode
        setIsEditing(false);
        console.log('No existing header found - creating new one');
      } else {
        setError("Failed to fetch header data. Please try again later.");
        console.error("Error fetching header:", err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for updating translations
  const handleTranslationChange = (lang, field, value) => {
    setHeaderData({
      ...headerData,
      translations: {
        ...headerData.translations,
        [lang]: {
          ...headerData.translations[lang],
          [field]: value
        }
      }
    });
  };

  // Handler for updating a single menu item
  const handleMenuChange = (lang, index, field, value) => {
    const updatedTranslations = { ...headerData.translations };
    const updatedMenus = [...updatedTranslations[lang].menus];
    
    updatedMenus[index] = {
      ...updatedMenus[index],
      [field]: value
    };
    
    // If parent field is changing, update isParent flag
    if (field === 'parent') {
      updatedMenus[index].isParent = !value || value === "";
    }
    
    updatedTranslations[lang] = {
      ...updatedTranslations[lang],
      menus: updatedMenus
    };
    
    setHeaderData({
      ...headerData,
      translations: updatedTranslations
    });
  };

  // Add a new menu item to the current language
  const addMenu = () => {
    const updatedTranslations = { ...headerData.translations };
    const currentMenus = [...updatedTranslations[language].menus];
    
    currentMenus.push({
      title: "",
      buttonTitle: "",
      link: "",
      openInNewTab: false,
      order: currentMenus.length + 1,
      parent: "",
      status: "active",
      isParent: true
    });
    
    updatedTranslations[language] = {
      ...updatedTranslations[language],
      menus: currentMenus
    };
    
    // Ensure the other language has the same number of menu items
    const otherLang = language === 'en' ? 'bn' : 'en';
    while (updatedTranslations[otherLang].menus.length < currentMenus.length) {
      updatedTranslations[otherLang].menus.push({
        title: "",
        buttonTitle: "",
        link: "",
        openInNewTab: false,
        order: updatedTranslations[otherLang].menus.length + 1,
        parent: "",
        status: "active",
        isParent: true
      });
    }
    
    setHeaderData({
      ...headerData,
      translations: updatedTranslations
    });
  };

  // Remove a menu item
  const removeMenu = (index) => {
    const updatedTranslations = { ...headerData.translations };
    
    // Get the menu title that's being removed (needed to update child references)
    const removedMenuTitle = updatedTranslations[language].menus[index].title;
    
    // Remove the menu item from all languages to keep them in sync
    Object.keys(updatedTranslations).forEach(lang => {
      // First, update any child menus that reference this menu as parent
      if (removedMenuTitle) {
        updatedTranslations[lang].menus = updatedTranslations[lang].menus.map(menu => {
          if (menu.parent === removedMenuTitle) {
            return {
              ...menu,
              parent: "",
              isParent: true
            };
          }
          return menu;
        });
      }
      
      // Then remove the menu
      updatedTranslations[lang].menus = updatedTranslations[lang].menus.filter((_, i) => i !== index);
      
      // Update the order of remaining menu items
      updatedTranslations[lang].menus = updatedTranslations[lang].menus.map((menu, i) => ({
        ...menu,
        order: i + 1
      }));
    });
    
    setHeaderData({
      ...headerData,
      translations: updatedTranslations
    });
  };

  // Submit handler - Create or Update header based on isEditing state
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Create a clean copy of the headerData without UI-specific properties
      const cleanTranslations = {};
      
      Object.keys(headerData.translations).forEach(lang => {
        cleanTranslations[lang] = {
          ...headerData.translations[lang],
          menus: headerData.translations[lang].menus.map(menu => {
            // Exclude the isParent field which is just for UI
            const { isParent, ...cleanMenu } = menu;
            return cleanMenu;
          })
        };
      });
      
      // Create FormData object to handle file uploads
      const formData = new FormData();
      
      // Add translations (which now include menus)
      formData.append('data', JSON.stringify({ translations: cleanTranslations }));
      
      // Add files if they exist
      if (headerData.logo) {
        formData.append('logo', headerData.logo);
      }
      
      if (headerData.contactIcon) {
        formData.append('contactIcon', headerData.contactIcon);
      }
      
      // Use the appropriate API endpoint based on whether we're creating or updating
      let response;
      if (isEditing) {
        // Update existing header
        response = await axios.put(
          `${API_URL}/api/header`, 
          formData, 
          { 
            headers: { 
              'x-api-key': API_KEY,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // Create new header
        response = await axios.post(
          `${API_URL}/api/header/add`, 
          formData, 
          { 
            headers: { 
              'x-api-key': API_KEY,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      // Update local state with the response
      setExistingHeaderData(response.data);
      setIsEditing(true);
      
      // Success notification or redirection could be added here
      toast.success(isEditing ? "Header updated successfully!" : "Header created successfully!");
      
    } catch (err) {
      console.error("Error saving header:", err);
      setError(err.response?.data?.error || "Failed to save header. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ----------------------------------------------------------------
  //  B) Language Switch (English <-> Bangla)
  // ----------------------------------------------------------------
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'bn', label: 'Bangla' }
  ];

  const handleLanguageChange = (selectedOption) => {
    setLanguage(selectedOption.value);
  };

  // Get only parent menus for the dropdown
  const getParentMenus = (lang, currentIndex) => {
    return headerData.translations[lang]?.menus?.filter((menu, index) => 
      index !== currentIndex && menu.isParent && menu.title
    ) || [];
  };

   // ─────────────────────────────────────────────────────────────────────────────
  // Render form
  // ─────────────────────────────────────────────────────────────────────────────
  const breadcrumbs = [
    { label: 'Dashboard', url: '/' },
    { label: 'Manage Sections'},
    { label: 'Header' }
  ];

  // Show loading state
  if (isLoading) {
    return (
      <div className="bg-gray-50">
        <PageHeading title="Manage Header" breadcrumbs={breadcrumbs} />
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading header data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className=" bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />
      <PageHeading title="Manage Header" breadcrumbs={breadcrumbs} />
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {/* Language Selection */}
      <div className="mb-6 flex">
        <DynamicSelect
          options={languageOptions}  // Pass options for languages
          value={languageOptions.find(option => option.value === language)}
          onChange={handleLanguageChange}
          isSearchable={false}
          placeholder="Select a language"
        />
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* TOP HEADER SECTION */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Top Header
                </h2>
              </div>
          {/* Logo */}
          <div className="mt-4 p-5">
            <ImageUpload
              label="Upload Logo"
              file={headerData.logo}
              setFile={(file) => setHeaderData({ ...headerData, logo: file })}
              imageUrl={existingHeaderData?.logo}
            />
          </div>

          {/* Contact Icon */}
          <div className="p-5">
            <ImageUpload
              label="Upload Contact Icon"
              file={headerData.contactIcon}
              setFile={(file) => setHeaderData({ ...headerData, contactIcon: file })}
              imageUrl={existingHeaderData?.contactIcon}
            />
          </div>

          {/* Phone Number */}
          <div className="p-5">
            <label className="label">Phone Number ({language})</label>
            <input
              type="tel"
              className="input-field"
              value={headerData.translations[language]?.phone || ''}
              onChange={(e) => handleTranslationChange(language, 'phone', e.target.value)}
              placeholder="Enter phone number"
            />
          </div>

          {/* Render contact icon & phone in the same link if phone is present */}
          {headerData.translations[language]?.phone && (
            <div className="px-5 pb-5">
              <a href={`tel:${headerData.translations[language].phone}`} className="flex items-center text-blue-500 gap-2">
                {existingHeaderData?.contactIcon && (
                  <img
                    src={`${API_URL}${existingHeaderData.contactIcon}`}
                    alt="Contact Icon"
                    className="w-6 h-6 object-cover border rounded-md"
                  />
                )}
                {headerData.contactIcon && (
                  <img
                    src={URL.createObjectURL(headerData.contactIcon)}
                    alt="Contact Icon"
                    className="w-6 h-6 object-cover border rounded-md"
                  />
                )}
                <span className="text-sm">{headerData.translations[language].phone}</span>
              </a>
            </div>
          )}
        </div>

        {/* MAIN HEADER MENUS */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Main Header Menus ({language})
            </h2>
          </div>
          {headerData.translations[language]?.menus?.map((menu, index) => (
            <div key={index} className="p-5">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-blue-700">
                  {menu.isParent ? "Parent Menu" : "Child Menu"}
                </span>
                {menu.parent && (
                  <span className="label">
                    Child of: {menu.parent}
                  </span>
                )}
              </div>
              
              <div className="flex flex-col md:flex-row gap-2 mb-2">
                <div className="flex-1">
                  <label className="label">
                    Menu Title
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={menu.title || ""}
                    onChange={(e) =>
                      handleMenuChange(language, index, "title", e.target.value)
                    }
                    placeholder="Menu Title"
                  />
                </div>
                <div className="flex-1">
                  <label className="label">
                    Menu Link
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={menu.link || ""}
                    onChange={(e) =>
                      handleMenuChange(language, index, "link", e.target.value)
                    }
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-2 mb-2">
                <div className="flex-1">
                  <div className="flex gap-1">
                    <input
                      type="checkbox"
                      checked={menu.openInNewTab || false}
                      className="w-3"
                      onChange={(e) =>
                        handleMenuChange(language, index, "openInNewTab", e.target.checked)
                      }
                    />
                    <label className="label mb-0">
                      Open in New Tab?
                    </label>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="label">
                    Menu Order
                  </label>
                  <input
                    type="number"
                    className="input-field"
                    value={menu.order || 1}
                    onChange={(e) =>
                      handleMenuChange(
                        language,
                        index,
                        "order",
                        parseInt(e.target.value) || 1
                      )
                    }
                    placeholder="1"
                  />
                </div>
                <div className="flex-1">
                  <label className="label">
                    Parent Menu
                  </label>
                  <DynamicSelect
                      value={
                        menu.parent
                          ? { value: menu.parent, label: menu.parent }
                          : null
                      }
                      options={[
                        { value: '', label: 'No Parent (This is a parent menu)' },
                        ...getParentMenus(language, index).map((parent) => ({
                          value: parent.title,
                          label: parent.title,
                        })),
                      ]}
                      onChange={(selectedOption) =>
                        handleMenuChange(language, index, 'parent', selectedOption?.value || '')
                      }
                      isSearchable={true}
                      placeholder="Select parent menu"
                  />
                </div>
                <div className="flex-1">
                  <label className="label">
                    Status
                  </label>
                  <DynamicSelect
                      value={{
                        value: menu.status || "active",
                        label: (menu.status || "active").charAt(0).toUpperCase() + (menu.status || "active").slice(1),
                      }}
                      options={[
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" },
                      ]}
                      onChange={(selectedOption) =>
                        handleMenuChange(language, index, "status", selectedOption?.value)
                      }
                      isSearchable={false}
                      placeholder="Select status"
                    />
                </div>
              </div>

              {/* Button Title */}
              <div className="mt-4">
                <label className="label">
                  Button Title
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={menu.buttonTitle || ""}
                  onChange={(e) =>
                    handleMenuChange(language, index, "buttonTitle", e.target.value)
                  }
                  placeholder="Button Title"
                />
              </div>

              <button
                type="button"
                onClick={() => removeMenu(index)}
                className="mt-2 text-red-500 flex items-center gap-1 text-[14px]"
              >
                <FaTrash size={12}/> Remove Menu
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addMenu}
            className="mt-4 text-blue-600 text-sm hover:text-blue-700 flex items-center gap-1 px-5 pb-5"
          >
            <FaPlus size={12}/> Add Menu
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 mt-5"
        >
          {isLoading ? 'Saving...' : isEditing ? 'Update Header' : 'Create Header'}
        </button>
      </form>
    </div>
  );
};

export default Header;